import { ChangeEvent, memo, useContext, useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { selectTraceDataBySnapshotId } from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import { getCenterWithLo, getCenterWithoutLo } from '../../utils/FreqUtils';

import { useAppSelector } from '../../app/hooks';
import {
  checkValidityOfAdjustmentValue,
  convertCrosspolAdjustmentFormValueForSaving,
  convertCrosspolAdjustmentFromDBForForm,
} from './utils/conversionsAndCalculations';
import { useIsAdmin } from '../auth/hooks/useIsAdmin';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useSwitches } from 'features/switches/api/getSwitches';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { grey } from '@mui/material/colors';
import { useIsGeoEnabled } from 'features/analyzerModules/api/getAnalyzerModules';
import { AnalyzerMode } from '../analyzers/types';
import CrosspolFreqOffset from './CrosspolFreqOffset';
import { SingleTraceViewContext } from 'features/singleTraceView/SingleTraceView';

interface Props {
  selectedAnalyzerId: number;
  selectedSnapshotId: number;
  crossPolSnapshotId: number;
}

const CrossPolDialog = memo(
  ({ selectedAnalyzerId, selectedSnapshotId, crossPolSnapshotId }: Props) => {
    const isAdmin = useIsAdmin();
    const [hasChanges, setHasChanges] = useState(false);
    const [crossPolAdjustment, setCrossPolAdjustment] = useState('0');
    const { crossPolSettings, setCrosspolSettings } = useContext(
      SingleTraceViewContext
    );

    const { data: switches } = useSwitches({});
    const { data: selectedAnalyzer } = useAnalyzer({
      analyzerId: selectedAnalyzerId,
    });
    const currentSwitch = switches?.find(
      (analyzerSwitch) =>
        analyzerSwitch.id === selectedAnalyzer?.analyzer_switch[0]?.switch_id
    );
    const isGeoEnabled = useIsGeoEnabled(selectedAnalyzerId);
    const { data: selectedSnapshot } = useSnapshot({
      snapshotId: selectedSnapshotId,
    });
    const { data: allSnapshotsForAnalyzer } = useSnapshots({
      analyzerId: selectedAnalyzerId,
    });
    const { data: crossPolSnapshot } = useSnapshot({
      snapshotId: crossPolSnapshotId,
    });
    const updateSnapshotMutation = useUpdateSnapshot({});

    const temporarilyDisabledSnapshots = allSnapshotsForAnalyzer?.filter(
      (snapshot) => Boolean(snapshot.temporarily_disabled)
    );

    const snapshotIsolated =
      temporarilyDisabledSnapshots && temporarilyDisabledSnapshots.length > 0;

    const data = useAppSelector((state) =>
      selectTraceDataBySnapshotId(state, selectedSnapshotId)
    );

    const [port, setPort] = useState(crossPolSnapshot?.port);
    const [switchPort, setSwitchPort] = useState(crossPolSnapshot?.switch_port);
    const [crossPolCenter, setCrosspolCenter] = useState<{
      value: string;
      unit: string;
    }>({
      value: '1030',
      unit: 'MHz',
    });

    const crossPolData = useAppSelector((state) =>
      selectTraceDataBySnapshotId(state, crossPolSnapshotId)
    );

    useEffect(() => {
      if (crossPolSnapshot?.crosspol_adjustment !== undefined)
        setCrossPolAdjustment(
          convertCrosspolAdjustmentFromDBForForm(
            crossPolSnapshot.crosspol_adjustment
          )
        );
      if (crossPolSnapshot?.port) setPort(crossPolSnapshot.port);
    }, [crossPolSnapshot?.crosspol_adjustment, crossPolSnapshot?.port]);

    useEffect(() => {
      if (!crossPolSnapshot) return;
      const splitFrequency = getCenterWithLo(
        crossPolSnapshot?.center,
        crossPolSnapshot?.lo,
        crossPolSnapshot?.lo_mode
      ).split(' ');
      if (!splitFrequency || splitFrequency.length < 2) return;
      setCrosspolCenter({
        value: splitFrequency[0],
        unit: splitFrequency[1],
      });
      //eslint-disable-next-line
    }, [crossPolSnapshot?.center]);

    const getCrossPolDelta = () => {
      if (!data || !crossPolData) return '';
      const adjustment = crossPolSnapshot?.crosspol_adjustment
        ? crossPolSnapshot.crosspol_adjustment / 1000
        : 0;
      if (crossPolSettings.peak_search) {
        const tracePeak = Math.max(...data);
        const coPolPeak = Math.max(...crossPolData);

        return (tracePeak - coPolPeak - adjustment).toFixed(2);
      } else {
        const center = Math.floor((data.length - 1) / 2);

        return (data[center] - crossPolData[center] - adjustment).toFixed(2);
      }
    };

    const handleCrossPolCenterValueChanged = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setCrosspolCenter((prevFreq) => ({
        ...prevFreq,
        value: event.target.value,
      }));
      setHasChanges(true);
    };

    const handleCrossPolCenterUnitChanged = (
      event: SelectChangeEvent<string>
    ) => {
      setCrosspolCenter((prevFreq) => ({
        ...prevFreq,
        unit: event.target.value,
      }));
      setHasChanges(true);
    };

    const handleChangePort = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setPort(Number(value));
      setHasChanges(true);
    };

    const handleSwitchPortChanged = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSwitchPort(Number(value));
      setHasChanges(true);
    };

    const handleChangeAdjustment = (event: ChangeEvent<HTMLInputElement>) => {
      setCrossPolAdjustment(event.target.value);
      setHasChanges(true);
    };

    const handlePeakSearchChecked = () => {
      const toggledPeakSearch = !crossPolSettings.peak_search;

      if (toggledPeakSearch) {
      } else {
      }

      setCrosspolSettings({
        ...crossPolSettings,
        peak_search: toggledPeakSearch,
      });
    };

    const handleSubmit = async () => {
      if (
        !checkValidityOfAdjustmentValue(crossPolAdjustment) ||
        !crossPolSnapshot
      )
        return;
      const levelForSaving =
        convertCrosspolAdjustmentFormValueForSaving(crossPolAdjustment);

      const currentCrossPolCenter = `${crossPolCenter.value} ${crossPolCenter.unit}`;
      const crossPolCenterWithoutLo = getCenterWithoutLo(
        currentCrossPolCenter,
        crossPolSnapshot.lo
      );

      await updateSnapshotMutation.mutateAsync({
        data: {
          crosspol_adjustment: levelForSaving,
          port,
          switch_port: switchPort,
          center: crossPolCenterWithoutLo,
        },
        snapshotId: crossPolSnapshot.id,
      });
      setHasChanges(false);
    };

    const handleCancel = () => {
      setCrossPolAdjustment(
        convertCrosspolAdjustmentFromDBForForm(
          crossPolSnapshot?.crosspol_adjustment
        )
      );
      setPort(crossPolSnapshot?.port);
      setHasChanges(false);
    };

    const handleIsolateSnapshot = async () => {
      if (temporarilyDisabledSnapshots && snapshotIsolated) {
        for (let i = 0; i < temporarilyDisabledSnapshots.length; i++) {
          await updateSnapshotMutation.mutateAsync({
            data: {
              ...temporarilyDisabledSnapshots[i],
              enabled: true,
              temporarily_disabled: false,
            },
            snapshotId: temporarilyDisabledSnapshots[i].id,
          });
        }

        return;
      }

      const snapshotsWithoutCrosspolSnapshots = allSnapshotsForAnalyzer?.filter(
        (snapshot) =>
          snapshot.id !== selectedSnapshotId &&
          snapshot.id !== crossPolSnapshotId
      );

      if (snapshotsWithoutCrosspolSnapshots === undefined) return;

      for (let i = 0; i < snapshotsWithoutCrosspolSnapshots.length; i++) {
        if (!snapshotsWithoutCrosspolSnapshots[i].enabled) continue;

        await updateSnapshotMutation.mutateAsync({
          data: {
            ...snapshotsWithoutCrosspolSnapshots[i],
            enabled: false,
            temporarily_disabled: true,
          },
          snapshotId: snapshotsWithoutCrosspolSnapshots[i].id,
        });
      }
    };

    return (
      <Grid
        container
        item
        style={{ width: '70%' }}
        spacing={2}
        direction='column'
      >
        <Grid item container spacing={1}>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              disabled={
                !checkValidityOfAdjustmentValue(crossPolAdjustment) ||
                !hasChanges
              }
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='secondary'
              disabled={!hasChanges}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Grid>
          {isAdmin &&
            isGeoEnabled &&
            selectedAnalyzer?.mode_before_crosspol !== AnalyzerMode.LIVE && (
              <Grid item>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleIsolateSnapshot}
                >
                  {snapshotIsolated ? 'Turn Off Isolation' : 'Isolate Snapshot'}
                </Button>
              </Grid>
            )}
        </Grid>
        <Grid item>
          <FormControlLabel
            value='peak_search_enabled'
            control={
              <Checkbox
                checked={crossPolSettings.peak_search}
                onChange={handlePeakSearchChecked}
              />
            }
            label='Peak Search'
          />
        </Grid>
        <Grid item>
          <TextField
            label={'Center'}
            variant='outlined'
            disabled
            size='small'
            value={
              selectedSnapshot
                ? getCenterWithLo(
                    selectedSnapshot.center,
                    selectedSnapshot.lo,
                    selectedSnapshot.lo_mode
                  )
                : ''
            }
            fullWidth
          />
        </Grid>
        <Grid item>
          <TextField
            label={'Co-Pol - Cross-Pol Delta'}
            variant='outlined'
            disabled
            size='small'
            value={getCrossPolDelta()}
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position='end'>dB</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant='outlined'
            fullWidth
            id='port'
            label='Cross-Pol Port'
            name='port'
            autoComplete='port'
            size='small'
            type='number'
            inputProps={{ min: '1', max: '16', step: '1' }}
            value={port || ''}
            onChange={handleChangePort}
          />
        </Grid>
        <Grid item>
          <CrosspolFreqOffset
            crossPolCenter={crossPolCenter}
            handleCrossPolCenterValueChanged={handleCrossPolCenterValueChanged}
            handleCrossPolCenterUnitChanged={handleCrossPolCenterUnitChanged}
          />
        </Grid>
        <Grid item>
          {selectedAnalyzer?.analyzer_switch[0]?.switch_channel ===
            Number(port) && (
            <TextField
              variant='outlined'
              required
              fullWidth
              id='switch_port'
              label='Switch Port'
              name='switch_port'
              autoComplete='switch_port'
              size='small'
              type='number'
              inputProps={{
                min: '1',
                max: currentSwitch?.number_of_ports?.toString(),
                step: '1',
              }}
              value={switchPort || ''}
              onChange={handleSwitchPortChanged}
            />
          )}
        </Grid>
        <Grid item>
          <TextField
            variant='outlined'
            fullWidth
            id='adjustment'
            label='Cross-Pol Adjustment'
            name='adjustment'
            autoComplete='adjustment'
            size='small'
            InputProps={{
              endAdornment: <InputAdornment position='end'>dB</InputAdornment>,
            }}
            value={crossPolAdjustment}
            onChange={handleChangeAdjustment}
          />
        </Grid>
        <Grid item>
          <Typography color={grey[500]}>
            You have switched to cross-pol mode. Your traces, monitors, masks,
            and markers are saved. You may change your original snapshot's
            settings in cross-pol mode, but changes may affect the snapshot's
            associated alarms.
          </Typography>
        </Grid>
      </Grid>
    );
  }
);

export default CrossPolDialog;
